import { useCallback, useEffect, useState } from 'react';
import {
  getTicketTaxQuote,
  getTicketTaxQuoteExpiresAt,
  type TicketTaxQuote,
} from './ticket-tax-quotes';

export type SelectedTicketQuote =
  | { status: 'idle' | 'loading' | 'error' }
  | {
      status: 'ready';
      ticketGroupId: number;
      quantity: number;
      quote: TicketTaxQuote;
      expiresAt: number;
    };

const IDLE_QUOTE: SelectedTicketQuote = { status: 'idle' };
const LOADING_QUOTE: SelectedTicketQuote = { status: 'loading' };

export function useSelectedTicketQuote(
  ticketGroupId: number | undefined,
  quantity: number
) {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    ticketGroupId: number;
    quantity: number;
    attempt: number;
    state: SelectedTicketQuote;
  }>();
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!ticketGroupId) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    getTicketTaxQuote(ticketGroupId, quantity).then(
      (quote) => {
        if (!active) return;
        const expiresAt = getTicketTaxQuoteExpiresAt(ticketGroupId, quantity);
        if (expiresAt <= Date.now()) {
          retry();
          return;
        }
        setResult({
          ticketGroupId,
          quantity,
          attempt,
          state: {
            status: 'ready',
            ticketGroupId,
            quantity,
            quote,
            expiresAt,
          },
        });
        timer = setTimeout(retry, expiresAt - Date.now());
      },
      () => {
        if (active)
          setResult({
            ticketGroupId,
            quantity,
            attempt,
            state: { status: 'error' },
          });
      }
    );
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [ticketGroupId, quantity, attempt, retry]);

  const state: SelectedTicketQuote = !ticketGroupId
    ? IDLE_QUOTE
    : result?.ticketGroupId === ticketGroupId &&
        result.quantity === quantity &&
        result.attempt === attempt &&
        (result.state.status !== 'ready' || result.state.expiresAt > Date.now())
      ? result.state
      : LOADING_QUOTE;
  return { state, retry };
}
